import { assertSuccessfulEventsResponse, extractUserIdsFromMentions, UnsupportedValueError, } from "../domain";
export function userIdsFromTransactions(transactions) {
    return transactions.reduce((userIds, t) => {
        // these are not necessarily userIds, but they *might* be
        if (t.from !== undefined) {
            userIds.add(t.from);
        }
        if (t.to !== undefined) {
            userIds.add(t.to);
        }
        return userIds;
    }, new Set());
}
export function userIdsFromEvents(events) {
    const userIds = new Set();
    const webhooks = new Set();
    const fakeFormatter = (k) => k;
    for (const e of events) {
        if ("userIds" in e.event) {
            e.event.userIds.forEach((u) => userIds.add(u));
        }
        switch (e.event.kind) {
            case "message":
                userIds.add(e.event.sender);
                if (e.event.senderContext?.kind === "webhook") {
                    webhooks.add(e.event.sender);
                }
                if (e.event.repliesTo !== undefined &&
                    e.event.repliesTo.kind === "rehydrated_reply_context") {
                    userIds.add(e.event.repliesTo.senderId);
                    extractUserIdsFromMentions(getContentAsFormattedText(fakeFormatter, e.event.repliesTo.content)).forEach((id) => userIds.add(id));
                }
                if (e.event.content.kind === "crypto_content") {
                    userIds.add(e.event.content.transfer.recipient);
                }
                else if (e.event.content.kind === "reported_message_content") {
                    e.event.content.reports.forEach((r) => userIds.add(r.reportedBy));
                }
                else if (e.event.content.kind === "moderation_report_content") {
                    userIds.add(e.event.content.sender);
                    e.event.content.reporters.forEach((r) => userIds.add(r));
                    if (e.event.content.status.kind !== "pending" &&
                        e.event.content.status.kind !== "contested") {
                        userIds.add(e.event.content.status.moderator);
                    }
                }
                else if (e.event.content.kind === "prize_winner_content") {
                    userIds.add(e.event.content.transaction.recipient);
                }
                else if (e.event.content.kind === "p2p_swap_content" &&
                    e.event.content.status.kind === "p2p_swap_accepted") {
                    userIds.add(e.event.content.status.acceptedBy);
                }
                else if (e.event.content.kind === "p2p_swap_content" &&
                    e.event.content.status.kind === "p2p_swap_reserved") {
                    userIds.add(e.event.content.status.reservedBy);
                }
                e.event.reactions.forEach((r) => r.userIds.forEach((u) => userIds.add(u)));
                extractUserIdsFromMentions(getContentAsFormattedText(fakeFormatter, e.event.content)).forEach((id) => userIds.add(id));
                break;
            case "member_joined":
            case "member_left":
                userIds.add(e.event.userId);
                break;
            case "name_changed":
            case "desc_changed":
            case "rules_changed":
            case "avatar_changed":
            case "role_changed":
            case "permissions_changed":
            case "group_visibility_changed":
            case "group_invite_code_changed":
                userIds.add(e.event.changedBy);
                break;
            case "group_chat_created":
                userIds.add(e.event.created_by);
                break;
            case "members_added":
                userIds.add(e.event.addedBy);
                break;
            case "members_removed":
                userIds.add(e.event.removedBy);
                break;
            case "users_blocked":
                userIds.add(e.event.blockedBy);
                break;
            case "users_unblocked":
                userIds.add(e.event.unblockedBy);
                break;
            case "message_pinned":
                userIds.add(e.event.pinnedBy);
                break;
            case "message_unpinned":
                userIds.add(e.event.unpinnedBy);
                break;
            case "events_ttl_updated":
            case "external_url_updated":
            case "gate_updated":
                userIds.add(e.event.updatedBy);
                break;
            case "users_invited":
                userIds.add(e.event.invitedBy);
                e.event.userIds.forEach((id) => userIds.add(id));
                break;
            case "chat_frozen":
                userIds.add(e.event.frozenBy);
                break;
            case "chat_unfrozen":
                userIds.add(e.event.unfrozenBy);
                break;
            case "bot_added":
                userIds.add(e.event.userId);
                userIds.add(e.event.addedBy);
                break;
            case "bot_removed":
                userIds.add(e.event.removedBy);
                break;
            case "bot_updated":
                userIds.add(e.event.userId);
                userIds.add(e.event.updatedBy);
                break;
            case "aggregate_common_events":
            case "direct_chat_created":
            case "empty":
            case "members_added_to_default_channel":
                break;
            default:
                console.warn("Unexpected ChatEvent type received", e.event);
        }
    }
    return {
        userIds,
        webhooks,
    };
}
export function getContentAsFormattedText(formatter, content, cryptoLookup = new Map()) {
    let text;
    if (content.kind === "text_content") {
        text = content.text;
    }
    else if (content.kind === "image_content") {
        text = captionedContent("Image", content.caption);
    }
    else if (content.kind === "video_content") {
        text = captionedContent("Video", content.caption);
    }
    else if (content.kind === "audio_content") {
        text = `${captionedContent("Audio", content.caption)} / ${Math.round(Number(content.durationMs) / 1000)}s`;
    }
    else if (content.kind === "file_content") {
        text = captionedContent(content.name, content.caption);
    }
    else if (content.kind === "crypto_content") {
        text = captionedContent(formatter("tokenTransfer.transfer", {
            values: { token: cryptoLookup.get(content.transfer.ledger)?.symbol ?? "Unknown" },
        }), content.caption);
    }
    else if (content.kind === "deleted_content" || content.kind === "blocked_content") {
        text = "Deleted message";
    }
    else if (content.kind === "placeholder_content") {
        text = "Placeholder content";
    }
    else if (content.kind === "bot_placeholder_content") {
        text = "Bot working ...";
    }
    else if (content.kind === "poll_content") {
        text = content.config.text ?? "Poll";
    }
    else if (content.kind === "proposal_content") {
        text = content.proposal.title;
    }
    else if (content.kind === "giphy_content") {
        text = captionedContent(formatter("giphyMessage"), content.caption);
    }
    else if (content.kind === "p2p_swap_content" || content.kind === "p2p_swap_content_initial") {
        text = captionedContent("p2p swap", content.caption);
    }
    else if (content.kind === "prize_content" || content.kind === "prize_content_initial") {
        text = captionedContent(formatter("prizeMessage"), content.caption);
    }
    else if (content.kind === "prize_winner_content") {
        text = "Prize winner message";
    }
    else if (content.kind === "message_reminder_content") {
        text = content.notes ?? "Message reminder";
    }
    else if (content.kind === "message_reminder_created_content") {
        text = content.notes ?? "Message reminder";
    }
    else if (content.kind === "user_referral_card") {
        text = formatter("referralHeader");
    }
    else if (content.kind === "reported_message_content") {
        text = "Reported message";
    }
    else if (content.kind === "meme_fighter_content") {
        text = "Meme Fighter message";
    }
    else if (content.kind === "daily_result") {
        text = captionedContent(`The Daily #${content.number}`, content.caption);
    }
    else if (content.kind === "video_call_content") {
        text = "Video call";
    }
    else if (content.kind === "encrypted_content") {
        text = "Encrypted message";
    }
    else if (content.kind === "moderation_report_content") {
        text = "Moderation report";
    }
    else if (content.kind === "restricted_content") {
        text = "Restricted message";
    }
    else if (content.kind === "price_chart_content") {
        text = content.title;
    }
    else if (content.kind === "mini_app_content") {
        text = content.text;
    }
    else {
        throw new UnsupportedValueError("Unrecognised content type", content);
    }
    return text.trim();
}
export function getContentAsText(content) {
    if ("text" in content) {
        return content.text;
    }
    else if ("caption" in content) {
        return content.caption;
    }
    else if (content.kind === "poll_content") {
        return content.config.text;
    }
}
function captionedContent(type, caption) {
    if (caption) {
        return type + " - " + caption;
    }
    else {
        return type;
    }
}
export function indexRangeForChat(chat) {
    return [getMinVisibleEventIndex(chat), chat.latestEventIndex];
}
export function getMinVisibleEventIndex(chat) {
    if (chat.kind === "direct_chat")
        return 0;
    return chat.minVisibleEventIndex;
}
export function getDisplayDate(chat) {
    let latestUpdate = BigInt(0);
    switch (chat.kind) {
        case "direct_chat":
            latestUpdate = chat.dateCreated;
            break;
        case "group_chat":
        case "channel":
            if (chat.membership?.joined !== undefined) {
                latestUpdate = chat.membership.joined;
            }
            break;
    }
    if (chat.latestMessage !== undefined && chat.latestMessage.timestamp > latestUpdate) {
        latestUpdate = chat.latestMessage.timestamp;
    }
    if (chat.eventsTtlLastUpdated > latestUpdate) {
        latestUpdate = chat.eventsTtlLastUpdated;
    }
    return latestUpdate;
}
export function compareChats(a, b) {
    return Number(getDisplayDate(b) - getDisplayDate(a));
}
export function emptyChatMetrics() {
    return {
        audioMessages: 0,
        edits: 0,
        cryptoMessages: 0,
        giphyMessages: 0,
        deletedMessages: 0,
        reportedMessages: 0,
        fileMessages: 0,
        pollVotes: 0,
        textMessages: 0,
        imageMessages: 0,
        replies: 0,
        videoMessages: 0,
        polls: 0,
        reactions: 0,
    };
}
export function emptyRules() {
    return {
        text: "",
        enabled: false,
        version: 0,
    };
}
export function compareRoles(a, b) {
    return a - b;
}
export { routeForChatIdentifier, routeForMessage, routeForMessageContext } from "./routes";
export function chatIdentifierToString(chatId) {
    switch (chatId.kind) {
        case "direct_chat":
            return chatId.userId;
        case "group_chat":
            return chatId.groupId;
        case "channel":
            return `${chatId.communityId}_${chatId.channelId}`;
        default:
            throw new UnsupportedValueError("Unknown chatId kind", chatId);
    }
}
// A chat whose latest message has disappeared has nothing left to read, whatever the
// read-up-to index says: every earlier message expired first, unless the TTL was shortened
// after they were sent. The summary keeps the expired message, so the unread count and the
// preview would otherwise point at messages that no longer exist.
export function latestMessageExpired(chat, now = Date.now()) {
    const expiresAt = chat.latestMessage?.expiresAt;
    return expiresAt !== undefined && expiresAt < now;
}
export function contentTypeToPermission(contentType) {
    switch (contentType) {
        case "image_content":
            return "image";
        case "video_content":
            return "video";
        case "audio_content":
            return "audio";
        case "file_content":
            return "file";
        case "giphy_content":
            return "giphy";
        case "crypto_content":
            return "crypto";
        case "p2p_swap_content_initial":
            return "p2pSwap";
        case "prize_content_initial":
            return "prize";
        case "daily_result":
            return "memeFighter";
        default:
            throw new UnsupportedValueError("Unknown attachment content type", contentType);
    }
}
export function isProposalsChat(chat) {
    return chat.kind !== "direct_chat" && chat.subtype?.kind === "governance_proposals";
}
export function mergeEventStreamResponses(current, next) {
    assertSuccessfulEventsResponse(next);
    const events = next.events;
    const indexes = new Set(events.map((e) => e.index));
    for (const event of current.events) {
        if (!indexes.has(event.index)) {
            indexes.add(event.index);
            events.push(event);
        }
    }
    events.sort((a, b) => b.index - a.index);
    return {
        events,
        expiredEventRanges: current.expiredEventRanges.concat(next.expiredEventRanges),
        latestEventIndex: next.latestEventIndex ?? current.latestEventIndex,
    };
}
